import { Button, Input } from '@casumo/dojo-react';
import { Close } from '@casumo/dojo-react-icons';
import { postcodeValidator } from 'postcode-validator';
import { ChangeEvent, memo, useMemo, useRef, useState } from 'react';
import Drawer from '../../design-system/drawer.tsx';
import useMarketInfo from '../../hooks/useMarketInfo.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TPostcodeInputField } from '../../types/types.ts';
import { TEST_ID } from '../../utils/constants.ts';
import { GTM_ERRORS } from '../../utils/gtm/constants.ts';
import { GtmEvents } from '../../utils/gtm/gtm.ts';

const PostcodeInput = ({
  name,
  value,
  label,
  validation,
  dialogTitle,
  dialogDescription,
  primaryButtonTitle,
  secondaryButtonTitle,
  onBlur,
  onKeyDown,
}: TPostcodeInputField & {
  onBlur?: (field: Partial<TPostcodeInputField>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  value?: string;
}) => {
  const { metadata, errors, setErrors, formData, setFormData } = useRegistrationStore();
  const { countryCode } = useMarketInfo();

  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [alertHasOpened, setAlertHasOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const postcode: string = useMemo(() => value || formData?.[name] || '', [value, formData, name]);

  const handleBlur = async () => {
    onBlur?.({ name, validation });

    setIsLoading(true);
    const isValid = postcodeValidator(postcode, countryCode);
    setIsLoading(false);

    if (isValid || alertHasOpened || !postcode) {

      return;
    }

    GtmEvents.logAlert({
      step:  metadata?.currentStep,
      alert: GTM_ERRORS.INVALID_POSTCODE,
    });

    setIsOpen(true);
    setAlertHasOpened(true);
    inputRef.current?.blur();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrors({ ...errors, [name]: '' });
    setFormData({ [name]: e.currentTarget.value });
  };

  const onEditPostcode = () => {
    setIsOpen(false);
    inputRef?.current?.focus();
  };

  const onSavePostcode = () => {
    setIsOpen(false);
    inputRef.current?.blur();
  };

  return (
    <>
      <Input
        id="postcode"
        ref={inputRef}
        name={name}
        label={label}
        value={postcode}
        error={errors[name] ? true : undefined}
        data-error={errors[name] ? true : undefined}
        errorMessage={errors[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={isLoading}
        clearable
        onClear={() => setFormData({ [name]: '' })}
        onKeyDown={onKeyDown}
        data-testid={TEST_ID.INPUT + 'postcode'}
        wrapperClass="hide-close-button-on-blur"
      />
      <Drawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        dialogClass="!h-fit md:min-w-[450px]">
        <div className="flex flex-col h-full w-full gap-lg p-sm justify-between overflow-hidden">
          <div>
            <div className="flex justify-between text-white pb-sm font-bold">
              <h1 className="text-h-lg">{dialogTitle}</h1>
              <span className="p-2xs cursor-pointer" onClick={onSavePostcode}><Close /></span>
            </div>
            <div className="text-white text-body-base">{dialogDescription}</div>
          </div>
          <div
            className="text-white text-[32px] h-full w-full flex items-center justify-center">{postcode}</div>
          <div className="flex md:flex-row flex-col justify-between gap-xs">
            <Button variant="primary-dark"
              className="w-full"
              onClick={onEditPostcode}
              data-testid={TEST_ID.BUTTON + 'postcode-change'}
            >
              {secondaryButtonTitle}
            </Button>
            <Button
              className="w-full"
              onClick={onSavePostcode}
              data-testid={TEST_ID.BUTTON + 'postcode-continue'}
            >
              {primaryButtonTitle}
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

const Memoized = memo(PostcodeInput);
Memoized.displayName = 'PostcodeInput';

export default Memoized;
